import React, { useRef, useState, useEffect } from 'react';

const FRAME_WIDTH = 1152;
const FRAME_HEIGHT = 2047;
const CROP_X = 60;
const CROP_Y = 100;
const CROP_WIDTH = 1030;
const CROP_HEIGHT = 1370;

const CameraCapture = () => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [imageBlob, setImageBlob] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // front camera by default

  const startCamera = async (mode = 'user') => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: mode },
    });
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play();
    }
  };

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      if (videoRef.current?.srcObject) {
        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
      }
    };
  }, [facingMode]);

  const flipCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  const capture = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    const frame = new Image();
    frame.src = '/frame.png';

    frame.onload = () => {
      canvas.width = FRAME_WIDTH;
      canvas.height = FRAME_HEIGHT;

      const videoRatio = video.videoWidth / video.videoHeight;
      const cutoutRatio = CROP_WIDTH / CROP_HEIGHT;

      let sx = 0, sy = 0, sw = video.videoWidth, sh = video.videoHeight;

      if (videoRatio > cutoutRatio) {
        const desiredWidth = video.videoHeight * cutoutRatio;
        sx = (video.videoWidth - desiredWidth) / 2;
        sw = desiredWidth;
      } else {
        const desiredHeight = video.videoWidth / cutoutRatio;
        sy = (video.videoHeight - desiredHeight) / 2;
        sh = desiredHeight;
      }

      context.drawImage(
        video,
        sx, sy, sw, sh,
        CROP_X, CROP_Y, CROP_WIDTH, CROP_HEIGHT
      );

      context.drawImage(frame, 0, 0, FRAME_WIDTH, FRAME_HEIGHT);

      const dataUrl = canvas.toDataURL('image/png');
      setCapturedImage(dataUrl);

      canvas.toBlob((blob) => {
        setImageBlob(blob);
      }, 'image/png');
    };
  };

  const download = () => {
    if (!capturedImage) return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      alert('📌 Long press the image to save it.');
      return;
    }
    const link = document.createElement('a');
    link.href = capturedImage;
    link.download = 'yoga-day-photo.png';
    link.click();
  };

  const shareWithSystem = async (platform) => {
    if (!imageBlob) return alert('Please capture a photo first.');
    const file = new File([imageBlob], 'photo.png', { type: 'image/png' });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: 'Yoga Day Photo',
          text: `🧘 Captured from Yoga Day Booth via ${platform}`,
        });
      } catch (err) {
        console.warn('Share cancelled or failed.', err);
      }
    } else {
      alert(`Sharing to ${platform} is not supported on this browser.\nYou can download and manually upload.`);
    }
  };

  return (
    <div style={capturedImage ? styles.scrollPage : styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>🧘 Moments from Oateo International Day of Yoga</h1>
        <p style={styles.subtitle}>#IDY2026</p>
      </header>

      <main style={styles.main}>
        {!capturedImage ? (
          <div style={styles.cameraContainer}>
            <video ref={videoRef} style={styles.video} playsInline muted />
            <img src="/frame.png" alt="frame overlay" style={styles.frame} />
          </div>
        ) : (
          <>
            <img src={capturedImage} alt="Captured" style={styles.capturedImage} />
            <p style={styles.hint}>📌 Long press to save if on iPhone</p>
          </>
        )}

        <div style={styles.buttonsContainer}>
          {!capturedImage ? (
            <>
              <button style={styles.captureButton} onClick={capture}>Capture</button>
              <button style={styles.button} onClick={flipCamera}>Flip Camera</button>
            </>
          ) : (
            <>
              <button style={styles.button} onClick={download}>Download</button>
              <button style={styles.button} onClick={() => shareWithSystem('WhatsApp')}>WhatsApp</button>
              <button style={styles.button} onClick={() => shareWithSystem('Facebook')}>Facebook</button>
              <button style={styles.button} onClick={() => shareWithSystem('Instagram')}>Instagram</button>
              <button style={styles.retakeButton} onClick={() => setCapturedImage(null)}>Retake</button>
            </>
          )}
        </div>
      </main>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

const styles = {
  page: {
    height: '100dvh',
    width: '100vw',
    background: '#fffdfc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: "'Inter', sans-serif",
    padding: '1rem',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  scrollPage: {
    height: '100%',
    width: '100vw',
    background: '#fffdfc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '1rem',
    fontFamily: "'Inter', sans-serif",
    boxSizing: 'border-box',
    overflowY: 'auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '0.8rem',
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: '600',
    letterSpacing: '0.5px',
    color: '#222',
    margin: 0,
  },
  subtitle: {
    fontSize: '1rem',
    color: '#999',
    fontStyle: 'italic',
    marginTop: '0.2rem',
  },
  main: {
    flex: 1,
    maxWidth: '480px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  cameraContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '420px',
    height: 'calc(100dvh - 280px)',
    borderRadius: '12px',
    backgroundColor: '#f8f8f8',
    border: '1px solid #eee',
    overflow: 'hidden',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  frame: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
  },
  capturedImage: {
    width: '100%',
    maxWidth: '430px',
    borderRadius: '10px',
    border: '1px solid #ddd',
  },
  hint: {
    fontSize: '0.85rem',
    color: '#666',
    marginTop: '0.5rem',
  },
  buttonsContainer: {
    marginTop: '1.2rem',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.6rem',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '420px',
    paddingBottom: 'env(safe-area-inset-bottom)',
  },
  captureButton: {
    flex: '1 1 100%',
    padding: '14px 0',
    borderRadius: '10px',
    backgroundColor: '#222',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
  },
  button: {
    flex: '1 1 48%',
    padding: '12px 0',
    borderRadius: '10px',
    backgroundColor: '#f0f0f0',
    color: '#111',
    fontSize: '0.95rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
  },
  retakeButton: {
    flex: '1 1 100%',
    padding: '12px 0',
    borderRadius: '10px',
    backgroundColor: '#ffe5e5',
    color: '#a00',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
  },
};

export default CameraCapture;
